import React from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

// DASH-EMAIL-012A: the real Broadcast attribution vocabulary
// (email_marketing_attribution.py's AttributionState). UNRESOLVED_BROADCAST/
// UNRESOLVED_CREATIVE/ATTRIBUTION_MISMATCH are reachable states on the real
// resolver but have no observed real-corpus example yet — rendered with the
// same neutral treatment as PARTIAL_RESOLUTION rather than assumed absent.
const ATTRIBUTION_BADGES = {
  RESOLVED: { label: "Resolved", className: "text-emerald-300", Icon: CheckCircle2 },
  PARTIAL_RESOLUTION: { label: "Partial", className: "text-amber-300", Icon: AlertTriangle },
  FINGERPRINT_UNAVAILABLE: { label: "Fingerprint Unavailable", className: "text-amber-300", Icon: AlertTriangle },
};
const DEFAULT_ATTRIBUTION_BADGE = { label: "Unresolved", className: "text-amber-300", Icon: HelpCircle };

/**
 * EmailCreativeVersionBroadcastTable — DASH-EMAIL-011b, cut over in
 * DASH-EMAIL-012A to the real per-Broadcast detail shape
 * (BroadcastDetailResponse). Deliberately narrower than the mock version:
 * the real API has no per-Broadcast Channel-2 metrics, execution timestamp
 * (`sentAt`), or `environment` field anywhere (see EMAIL-CATALOG-007A's own
 * response models) — those columns are removed rather than rendered with a
 * fabricated/placeholder value. "View in Performance" is removed too:
 * Performance still runs on its own independent synthetic Broadcast catalog
 * (DASH-EMAIL-012 §11), so a real BRC#### id has no honest Performance
 * counterpart today — restoring this is explicit follow-up work, not part
 * of this checkpoint.
 *
 * Attribution (RESOLVED/PARTIAL_RESOLUTION/FINGERPRINT_UNAVAILABLE) is the
 * EMAIL-CATALOG-006A resolver's own output, reused unmodified here — this
 * component performs no resolution logic of its own, only presentation.
 * Recipient Snapshot honesty (DASH-EMAIL-012A Frozen Decision 19): a false
 * `recipient_snapshot_available` renders "Not captured", NEVER "0" — a
 * historical gap in what was recorded is not the same fact as zero
 * recipients.
 */
export default function EmailCreativeVersionBroadcastTable({ broadcasts, loading, error }) {
  if (loading) {
    return (
      <p className="text-xs text-slate-500" data-testid="email-creative-version-broadcasts-loading">
        Loading Broadcast usage…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-xs text-red-300" data-testid="email-creative-version-broadcasts-error">
        Failed to load Broadcast usage: {error}
      </p>
    );
  }

  const rows = broadcasts || [];

  if (rows.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="email-creative-version-broadcasts-empty">
        No Broadcasts have used this Version yet.
      </p>
    );
  }

  return (
    <div data-testid="email-creative-version-broadcast-table">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Broadcasts Using This Version</p>
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-slate-400">Broadcast</TableHead>
            <TableHead className="text-slate-400">Status</TableHead>
            <TableHead className="text-slate-400">Attribution</TableHead>
            <TableHead className="text-right text-slate-400">Recipients</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const badge = ATTRIBUTION_BADGES[row.attribution?.attribution_state] || DEFAULT_ATTRIBUTION_BADGE;
            const Icon = badge.Icon;
            const snapshotAvailable = row.recipient_snapshot?.recipient_snapshot_available;
            return (
              <TableRow key={row.broadcast_id} className="border-white/10 hover:bg-white/5" data-testid={`email-creative-version-broadcast-row-${row.broadcast_id}`}>
                <TableCell className="text-sm font-medium text-slate-100">
                  {row.name || row.broadcast_id}
                  <div className="text-xs text-slate-400">{row.broadcast_id}</div>
                </TableCell>
                <TableCell className="text-xs text-slate-300">{row.status}</TableCell>
                <TableCell>
                  <span
                    className={`flex items-center gap-1 text-xs ${badge.className}`}
                    data-testid={`email-creative-version-broadcast-attribution-${row.broadcast_id}`}
                  >
                    <Icon className="h-3 w-3" />
                    {badge.label}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono text-slate-100" data-testid={`email-creative-version-broadcast-recipients-${row.broadcast_id}`}>
                  {snapshotAvailable ? Number(row.recipient_snapshot.recipient_count || 0).toLocaleString() : (
                    <span className="text-slate-500">Not captured</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
